import type { ReportRow } from 'src/components/reports/types';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';

interface UseRelationshipActionsOptions {
  repositoryId: UUID | null;
  row: ReportRow;
}

export function useRelationshipActions({ repositoryId, row }: UseRelationshipActionsOptions) {
  const currentUser = useSelectCurrentUser();
  const [confirmRelationship, { loading: confirming }] =
    Apollo.useConfirmDocumentRelationshipMutation();
  const [rejectRelationship, { loading: rejecting }] =
    Apollo.useRejectDocumentRelationshipMutation();
  const [restoreRelationship, { loading: undoing }] =
    Apollo.useRestoreDocumentRelationshipMutation();
  const [setParentAndConfirm, { loading: settingParent }] =
    Apollo.useSetDocumentRelationshipParentAndConfirmMutation();
  const [setChildAndConfirm, { loading: settingChild }] =
    Apollo.useSetDocumentRelationshipChildAndConfirmMutation();
  const [updateRelationshipType, { loading: updatingType }] =
    Apollo.useUpdateDocumentRelationshipTypeMutation();
  const isMutating = confirming || rejecting || updatingType || undoing;

  function getRelationshipId(): string | null {
    switch (row.__typename) {
      case 'Document':
        return row.relationship?.id ?? null;
      case 'UrlRelationshipRow':
        return row.urlRelationship.id;
      case 'OrphanRelationshipRow':
        return row.orphanedRelationship.id;
      case 'GroupHeaderRow':
        return null;
    }
  }

  /** Row-based relationship data for building optimistic mutation responses. */
  function getRelationship() {
    switch (row.__typename) {
      case 'Document':
        return row.relationship ?? null;
      case 'UrlRelationshipRow':
        return row.urlRelationship;
      case 'OrphanRelationshipRow':
        return row.orphanedRelationship;
      case 'GroupHeaderRow':
        return null;
    }
  }

  async function handleConfirmRelationship(): Promise<void> {
    const relId = getRelationshipId();
    if (!relId) return;

    const rel = getRelationship();
    await confirmRelationship({
      optimisticResponse:
        currentUser && rel
          ? {
              __typename: 'Mutation',
              confirmDocumentRelationship: {
                __typename: 'DocumentRelationship',
                childDocumentId: rel.childDocumentId,
                childRelationshipType: rel.childRelationshipType,
                id: relId,
                parentDocumentId: rel.parentDocumentId,
                review: {
                  __typename: 'DocumentRelationshipReview',
                  outcome: Apollo.ReviewOutcome.Confirmed,
                  reviewedBy: {
                    __typename: 'User',
                    displayName: currentUser.displayName,
                    id: currentUser.id,
                  },
                },
                source: rel.source,
              },
            }
          : undefined,
      variables: { id: relId },
    });
  }

  async function handleRejectRelationship(): Promise<void> {
    const relId = getRelationshipId();
    if (!relId) return;
    const rel = getRelationship();
    await rejectRelationship({
      optimisticResponse:
        currentUser && rel
          ? {
              __typename: 'Mutation',
              rejectDocumentRelationship: {
                __typename: 'DocumentRelationship',
                childDocumentId: rel.childDocumentId,
                childRelationshipType: rel.childRelationshipType,
                id: relId,
                parentDocumentId: rel.parentDocumentId,
                review: {
                  __typename: 'DocumentRelationshipReview',
                  outcome: Apollo.ReviewOutcome.Rejected,
                  reviewedBy: {
                    __typename: 'User',
                    displayName: currentUser.displayName,
                    id: currentUser.id,
                  },
                },
                source: rel.source,
              },
            }
          : undefined,
      variables: { id: relId },
    });
  }

  async function handleSetParentAndConfirm(
    newParentDocumentId: UUID,
    relationshipIdOverride?: UUID
  ): Promise<void> {
    const relId = relationshipIdOverride ?? getRelationshipId();
    if (!relId || !repositoryId) return;

    await setParentAndConfirm({
      variables: { id: relId, parentDocumentId: newParentDocumentId },
    });
  }

  async function handleSetChildAndConfirm(
    newChildDocumentId: UUID,
    relationshipIdOverride?: UUID
  ): Promise<void> {
    const relId = relationshipIdOverride ?? getRelationshipId();
    if (!relId) return;

    await setChildAndConfirm({
      variables: { childDocumentId: newChildDocumentId, id: relId },
    });
  }

  async function handleUpdateRelationshipType(
    newType: Apollo.ChildRelationshipType
  ): Promise<void> {
    const rel = getRelationship();
    if (!rel) return;

    await updateRelationshipType({
      optimisticResponse: {
        __typename: 'Mutation',
        updateDocumentRelationshipType: {
          __typename: 'DocumentRelationship',
          childDocumentId: rel.childDocumentId,
          childRelationshipType: newType,
          id: rel.id,
          parentDocumentId: rel.parentDocumentId,
          review: rel.review,
          source: rel.source,
        },
      },
      variables: { childRelationshipType: newType, id: rel.id },
    });
  }

  async function handleUndoRelationship(relationshipIdOverride?: string): Promise<void> {
    const relId = relationshipIdOverride ?? getRelationshipId();
    if (!relId) return;
    const rel = getRelationship();
    await restoreRelationship({
      optimisticResponse: rel
        ? {
            __typename: 'Mutation',
            restoreDocumentRelationship: {
              __typename: 'DocumentRelationship',
              childDocumentId: rel.childDocumentId,
              childRelationshipType: rel.childRelationshipType,
              id: relId,
              parentDocumentId: rel.parentDocumentId,
              review: null,
              source: rel.source,
            },
          }
        : undefined,
      variables: { id: relId },
    });
  }

  return {
    confirming,
    handleConfirmRelationship,
    handleRejectRelationship,
    handleSetChildAndConfirm,
    handleSetParentAndConfirm,
    handleUndoRelationship,
    handleUpdateRelationshipType,
    isMutating,
    rejecting,
    settingChild,
    settingParent,
    undoing,
    updatingType,
  };
}
